import { type RefObject, useCallback, useEffect, useMemo } from 'react';

import { centerPlayerCameraOnTarget } from '../lib/centerPlayerCameraOnTarget';
import { usePlayerStoreApi } from '../store/playerStore';
import type {
  PlayerCameraControlsHandle,
  PlayerCameraInputMode,
  PlayerCenterOnTargetOptions,
  PlayerFollowTarget,
} from '../types';
import { usePlayerCameraControlsContext } from './playerCameraControlsContext';
import { usePlayerSettings } from './usePlayerSettings';

export interface UsePlayerCameraControlsOptions {
  cameraControlsRef?: RefObject<PlayerCameraControlsHandle | null>;
}

function applyInputMode(
  cameraControls: PlayerCameraControlsHandle | null,
  inputMode: PlayerCameraInputMode,
) {
  if (!cameraControls) {
    return;
  }

  if (inputMode === 'truck') {
    cameraControls.mouseButtons.left = 2;
    cameraControls.mouseButtons.right = 1;
    cameraControls.mouseButtons.middle = 8;
    return;
  }

  if (inputMode === 'rotate') {
    cameraControls.mouseButtons.left = 1;
    cameraControls.mouseButtons.right = 2;
    cameraControls.mouseButtons.middle = 8;
    return;
  }

  cameraControls.mouseButtons.left = 8;
  cameraControls.mouseButtons.right = 2;
  cameraControls.mouseButtons.middle = 1;
}

export function usePlayerCameraControls({
  cameraControlsRef,
}: UsePlayerCameraControlsOptions = {}) {
  const store = usePlayerStoreApi();
  const {
    cameraControls: contextCameraControls,
    inputMode,
    setInputMode,
    settings: {
      camera: { follow },
    },
    setSettings,
  } = {
    ...usePlayerCameraControlsContext(),
    ...usePlayerSettings(),
  };

  const cameraControls = cameraControlsRef?.current ?? contextCameraControls;

  useEffect(() => {
    applyInputMode(
      cameraControlsRef?.current ?? contextCameraControls,
      inputMode,
    );
  }, [cameraControlsRef, contextCameraControls, inputMode]);

  const setFollow = useCallback(
    (cameraFollow: boolean) => {
      setSettings({
        camera: {
          follow: cameraFollow,
        },
      });
    },
    [setSettings],
  );

  const zoomIn = useCallback(() => {
    const controls = cameraControlsRef?.current ?? contextCameraControls;
    controls?.cancelAutoFit?.();
    controls?.dolly(1, true);
  }, [cameraControlsRef, contextCameraControls]);

  const zoomOut = useCallback(() => {
    const controls = cameraControlsRef?.current ?? contextCameraControls;
    controls?.cancelAutoFit?.();
    controls?.dolly(-1, true);
  }, [cameraControlsRef, contextCameraControls]);

  const frame = useCallback(() => {
    (cameraControlsRef?.current ?? contextCameraControls)?.frame?.(true);
  }, [cameraControlsRef, contextCameraControls]);

  const resetCamera = useCallback(() => {
    const controls = cameraControlsRef?.current ?? contextCameraControls;
    controls?.cancelAutoFit?.();
    controls?.reset(true);
  }, [cameraControlsRef, contextCameraControls]);

  const centerOnTarget = useCallback(
    (
      target?: PlayerFollowTarget,
      options: PlayerCenterOnTargetOptions = {},
    ) => {
      const controls = cameraControlsRef?.current ?? contextCameraControls;
      controls?.cancelAutoFit?.();
      centerPlayerCameraOnTarget({
        cameraControls: controls,
        store,
        target,
        options,
      });
    },
    [cameraControlsRef, contextCameraControls, store],
  );

  return useMemo(
    () => ({
      cameraControls,
      hasCameraControls: cameraControls !== null,
      inputMode,
      setInputMode,
      follow,
      setFollow,
      zoomIn,
      zoomOut,
      frame,
      resetCamera,
      centerOnTarget,
    }),
    [
      cameraControls,
      centerOnTarget,
      frame,
      follow,
      inputMode,
      resetCamera,
      setInputMode,
      setFollow,
      zoomIn,
      zoomOut,
    ],
  );
}
